import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileSignature, FileText, AlertTriangle } from "lucide-react";

const dataOra = (s) =>
  s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

/** Archivio delle informative firmate dai locali: chi ha firmato, quando, con quale versione. */
export const FirmeLegali = ({ client }) => {
  const [d, setD] = useState(null);

  useEffect(() => {
    client.get("/admin/firme").then((r) => setD(r.data)).catch(() => setD({ rows: [], missing: [] }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const apri = async (tid) => {
    const r = await client.get(`/admin/firme/${tid}.pdf`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };

  if (!d) return <p className="text-sm text-slate-400">Carico l'archivio firme…</p>;

  return (
    <Card className="space-y-4 border-slate-800 bg-[#111827] p-5 text-slate-100" data-testid="firme-legali">
      <div>
        <h2 className="font-head flex items-center gap-2 text-lg font-bold">
          <FileSignature className="h-4 w-4" /> Informative firmate
        </h2>
        <p className="mt-0.5 text-xs text-slate-400">
          Versione corrente {d.version} del {d.date} · {d.rows.length} firme in archivio
        </p>
      </div>

      <div className="space-y-2">
        {d.rows.length === 0 && <p className="text-sm text-slate-400">Nessuna firma ancora registrata.</p>}
        {d.rows.map((r) => (
          <div
            key={`${r.tenant_id}-${r.version}`}
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-900/60 p-3"
            data-testid={`firma-riga-${r.slug || r.tenant_id}`}
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{r.tenant || r.slug}</p>
              <p className="text-xs text-slate-400">
                {r.by}{r.role ? ` · ${r.role}` : ""} · {dataOra(r.at)} · v{r.version}
                {r.version !== d.version && <span className="ml-1 text-amber-400">(da rifirmare)</span>}
              </p>
              <p className="truncate text-[11px] text-slate-500">
                {r.email || "senza email"} · IP {r.ip || "—"} · inviata a {r.sent_to || "—"}
              </p>
              <p className="truncate font-mono text-[10px] text-slate-600">SHA-256 {r.hash}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => apri(r.tenant_id)} data-testid={`firma-pdf-${r.slug || r.tenant_id}`}>
              <FileText className="mr-1.5 h-4 w-4" /> PDF firmato
            </Button>
          </div>
        ))}
      </div>

      {d.missing?.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3" data-testid="firme-mancanti">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-300">
            <AlertTriangle className="h-4 w-4" /> Non hanno ancora firmato la versione {d.version}
          </p>
          <ul className="mt-1 space-y-0.5 text-xs text-amber-100/80">
            {d.missing.map((m) => (
              <li key={m.tenant_id}>· {m.tenant || m.slug} {m.email ? `(${m.email})` : ""}</li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-amber-100/60">
            Al primo accesso del titolare la firma viene chiesta prima della configurazione.
          </p>
        </div>
      )}
    </Card>
  );
};
